import React, { useEffect, useRef } from 'react';
import { Star, Paperclip } from 'lucide-react';
import { fmtMailDate, JobChip } from './mailUtils';

export const MailThreadList = ({ threads, selectedId, onSelect, onStar, loading, emptyText = 'Nothing here', compact = false,
  selectable = false, selectedIds, onToggleSelect, focusedId }) => {
  const focusRef = useRef(null);
  useEffect(() => { focusRef.current?.scrollIntoView({ block: 'nearest' }); }, [focusedId]);
  if (loading && !threads.length) {
    return <div className="p-4 space-y-3" data-testid="mail-list-loading">{[1, 2, 3, 4, 5].map(i => <div key={i} className="h-12 rounded-lg bg-slate-100 animate-pulse" />)}</div>;
  }
  if (!threads.length) return <p className="text-sm text-slate-400 text-center py-10" data-testid="mail-list-empty">{emptyText}</p>;
  return (
    <div className="divide-y divide-slate-200/60" data-testid="mail-thread-list">
      {threads.map(t => {
        const selected = selectedId === t.id;
        const checked = selectedIds?.has(t.id);
        const focused = focusedId === t.id;
        return (
          <div key={t.id} ref={focused ? focusRef : null} role="button" tabIndex={-1} onClick={() => onSelect(t)} data-testid={`mail-thread-${t.id}`}
            className={`w-full text-left px-2 py-2.5 flex items-start gap-2 transition-colors border-l-2 cursor-pointer ${selected ? 'bg-sky-50 border-sky-500' : checked ? 'bg-amber-50/70 border-amber-300' : focused ? 'bg-white border-slate-400' : 'border-transparent hover:bg-white'}`}>
            {selectable && (
              <input type="checkbox" checked={!!checked} onChange={() => onToggleSelect(t.id)} onClick={e => e.stopPropagation()} className="mt-1.5 w-3.5 h-3.5 accent-sky-600 shrink-0" aria-label="Select conversation" data-testid={`mail-select-${t.id}`} />
            )}
            <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${t.unread ? 'bg-red-500 shadow-[0_0_0_3px_rgba(239,68,68,0.15)]' : 'bg-transparent'}`} data-testid={t.unread ? `mail-unread-dot-${t.id}` : undefined} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className={`text-xs truncate ${t.unread ? 'text-slate-900 font-bold' : 'text-slate-600'}`}>{t.people.join(', ')}{t.count > 1 ? <span className="text-slate-500 font-normal"> ({t.count})</span> : null}</span>
                {t.has_attachments && <Paperclip className="w-3 h-3 text-slate-400 shrink-0" />}
                <span className="ml-auto text-[10px] text-slate-500 shrink-0 tabular-nums">{fmtMailDate(t.date)}</span>
              </div>
              <div className={`text-sm truncate ${t.unread ? 'text-slate-900 font-semibold' : 'text-slate-700'}`}>{t.subject}</div>
              {!compact && <div className="text-xs text-slate-500 truncate">{t.snippet}</div>}
              {t.chips?.length > 0 && <div className="flex flex-wrap gap-1 mt-1">{t.chips.map(c => <JobChip key={c.ref} chip={c} />)}</div>}
            </div>
            {onStar && (
              <span role="button" onClick={e => { e.stopPropagation(); onStar(t); }} className={`mt-0.5 p-1 rounded hover:bg-slate-900/5 ${t.starred ? 'text-amber-500' : 'text-slate-300 hover:text-slate-500'}`} data-testid={`mail-star-${t.id}`}>
                <Star className={`w-3.5 h-3.5 ${t.starred ? 'fill-amber-400' : ''}`} />
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
};
